import { analytics } from '@dashboard-experience/utils';
import {
  identify as ampIdentify,
  Identify,
} from '@amplitude/analytics-browser';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';

import { queryReportCache } from '../api/report/queryCache';
import { getCurrentReportId } from '../api/report/currentReport';
import { AMPLITUDE_API_KEY, APP_VERSION, CHECKR_REGION } from '../constants';

countries.registerLocale(enLocale);

const services = [
  {
    name: 'amplitude',
    apiKey: AMPLITUDE_API_KEY,
  },
];

// analytics(services) should only be run on client side
// since it requires sessionStorage

let analyticsCheck;

try {
  analyticsCheck = analytics(services);
} catch {
  // eslint-disable-next-line no-console
  console.log('Analytics disabled due to cookies being disabled');
}
const checkrAnalytics = analyticsCheck;

const ANALYTICS_EVENTS = {
  CANDIDATE_STORY_SHARED: 'Candidate Portal Story Shared',
  EXCEPTION_FLOW_INFORMATION_COMPLETED:
    'Candidate Portal Exception Information Completed',
  EXCEPTION_FLOW_WELCOME_PAGE_VIEWED:
    'Candidate Portal Exception Welcome Page Viewed',
  FAQ_SECTION_LINK_CLICKED: 'Candidate Portal FAQ Viewed',
  HELP_CENTER_VIEWED: 'Candidate Portal Help Center Viewed',
  LANDING_PAGE_VIEWED: 'Candidate Portal Landing Page Viewed',
  LANGUAGE_SELECTED: 'Candidate Portal Language Selected',
  MULTIDOC_DOCUMENTS_CLOSED: 'Candidate Portal Multidoc Documents Closed',
  MULTIDOC_DOCUMENTS_LABELED: 'Candidate Portal Multidoc Documents Labeled',
  MULTIDOC_EXPLANATION_CLOSED: 'Candidate Portal Multidoc Explanation Closed',
  MULTIDOC_EXPLANATION_SUBMITTED:
    'Candidate Portal Multidoc Explanation Submitted',
  MULTIDOC_PAGE_VIEWED: 'Candidate Portal Multidoc Page Viewed',
  MULTIDOC_ERROR_MODAL_OPENED: 'Candidate Portal Multidoc Error Modal Opened',
  MULTIDOC_ERROR_MODAL_CLOSED: 'Candidate Portal Multidoc Error Modal Closed',
  MULTIDOC_ADD_DOCUMENT_MODAL_OPENED:
    'Candidate Portal Multidoc Add Document Modal Opened',
  MULTIDOC_ADD_DOCUMENT_MODAL_CLOSED:
    'Candidate Portal Multidoc Add Document Modal Closed',
  NOT_YOU_BUTTON_CLICKED: 'Candidate Portal Exception Not You Button Clicked',
  PERSONAL_INFO_UPDATED: 'Candidate Portal Personal Information Updated',
  REPORT_DOWNLOADED: 'Candidate Portal Report Downloaded',
  REPORT_ERROR_BUTTON_CLICKED: 'Candidate Portal Error Reported',
  REPORT_VIEWED: 'Candidate Portal Report Viewed',
  SCREENING_DOWNLOADED: 'Candidate Portal Screening Downloaded',
  CANDIDATE_PORTAL_LOGIN_PAGE_VIEWED: 'Candidate Portal Login Page Viewed',
  CANDIDATE_PORTAL_CHECKR_PROFILE_AUTH_LOGIN_SUCCESS:
    'Candidate Portal Checkr Profile Auth Login Success',
  CANDIDATE_PORTAL_SSN_LOGIN_SUBMITTED: 'Candidate Portal SSN Login Submitted',
  CANDIDATE_PORTAL_SSN_LOGIN_SUCCESS: 'Candidate Portal SSN Login Success',
  CANDIDATE_PORTAL_SSN_LOGIN_FAILED: 'Candidate Portal SSN Login Failed',
  CANDIDATE_PORTAL_INTERNATIONAL_LOGIN_SUBMITTED:
    'Candidate Portal International Login Submitted',
  CANDIDATE_PORTAL_INTERNATIONAL_LOGIN_SUCCESS:
    'Candidate Portal International Login Success',
  CANDIDATE_PORTAL_INTERNATIONAL_LOGIN_FAILED:
    'Candidate Portal International Login Failed',
  CANDIDATE_PORTAL_PASSWORDLESS_LOGIN_VIEWED:
    'Candidate Portal Passwordless Login Viewed',
  CANDIDATE_PORTAL_PASSWORDLESS_LOGIN_CLICKED:
    'Candidate Portal Passwordless Login Clicked',
  SSN_EXCEPTION_CHECK_NOT_COMPLETED: 'SSN Exception Check Not Completed',
  SSN_EXCEPTION_ID_DOC_UPLOAD_COMPLETED:
    'SSN Exception ID Doc Upload Completed',
  SSN_EXCEPTION_ID_DOC_UPLOAD_VIEWED: 'SSN Exception ID Doc Upload Viewed',
  SSN_EXCEPTION_INFO_RECEIVED_VIEWED: 'SSN Exception Info Received Viewed',
  SSN_EXCEPTION_POLLING_COMPLETED: 'SSN Exception Polling Completed',
  SSN_EXCEPTION_SSN_CARD_UPLOAD_COMPLETED:
    'SSN Exception SSN Card Upload Completed',
  SSN_EXCEPTION_SSN_CARD_UPLOAD_VIEWED: 'SSN Exception SSN Card Upload Viewed',
  SSN_EXCEPTION_SSN_REENTRY_COMPLETED: 'SSN Exception SSN Reentry Completed',
  SSN_EXCEPTION_SSN_REENTRY_VIEWED: 'SSN Exception SSN Reentry Viewed',
  SSN_EXCEPTION_UPLOAD_DOC_LATER_VIEWED:
    'SSN Exception Upload Doc Later Viewed',
  SSN_EXCEPTION_WAITING_SCREEN_VIEWED: 'SSN Exception Waiting Screen Viewed',
  APPLY_FLOW_BACKGROUND_CHECK_STARTED: 'Apply Flow Background Check Started',
  SSN_EXCEPTION_CONTACT_CHECKR_VIEWED: 'SSN Exception Contact Checkr Viewed',
  CANDIDATE_PORTAL_WORKOS_LOGIN_PAGE_VIEWED:
    'Candidate Portal WorkOS Login Page Viewed',
  CANDIDATE_PORTAL_WORKOS_CODE_PAGE_VIEWED:
    'Candidate Portal WorkOS Code Page Viewed',
  CANDIDATE_PORTAL_WORKOS_DOB_PAGE_VIEWED:
    'Candidate Portal WorkOS DOB Page Viewed',
  CANDIDATE_PORTAL_WORKOS_DOB_SUBMITTED:
    'Candidate Portal WorkOS DOB Submitted',
  CANDIDATE_PORTAL_WORKOS_DOB_VERIFY_FAILED:
    'Candidate Portal WorkOS DOB Verify Failed',
  CANDIDATE_PORTAL_WORKOS_LOGIN_SUBMITTED:
    'Candidate Portal WorkOS Login Submitted',
  CANDIDATE_PORTAL_WORKOS_LOGIN_FAILED: 'Candidate Portal WorkOS Login Failed',
  CANDIDATE_PORTAL_WORKOS_RESEND_CODE_CLICKED:
    'Candidate Portal WorkOS Resend Code Clicked',
  CANDIDATE_PORTAL_WORKOS_BACK_TO_EMAIL_CLICKED:
    'Candidate Portal WorkOS Back To Email Clicked',
  CANDIDATE_PORTAL_WORKOS_SSN_FALLBACK_CLICKED:
    'Candidate Portal WorkOS SSN Fallback Clicked',
  CANDIDATE_PORTAL_WORKOS_GOOGLE_OAUTH_INITIATED:
    'Candidate Portal WorkOS Google OAuth Initiated',
  CANDIDATE_PORTAL_WORKOS_GOOGLE_OAUTH_FAILED:
    'Candidate Portal WorkOS Google OAuth Failed',
  CANDIDATE_PORTAL_WORKOS_EMAIL_SUGGESTION_SHOWN:
    'Candidate Portal WorkOS Email Suggestion Shown',
  CANDIDATE_PORTAL_WORKOS_EMAIL_SUGGESTION_ACCEPTED:
    'Candidate Portal WorkOS Email Suggestion Accepted',
  CANDIDATE_PORTAL_WORKOS_EMAIL_SUGGESTION_DISMISSED:
    'Candidate Portal WorkOS Email Suggestion Dismissed',
  CANDIDATE_PORTAL_SESSION_EXPIRED: 'Candidate Portal Session Expired',
  CANDIDATE_PORTAL_LOGIN_SUCCESS: 'Candidate Portal Login Success',
  DISPUTE_STARTED: 'Candidate Portal Dispute Started',
  DISPUTE_STEP_VIEWED: 'Candidate Portal Dispute Step Viewed',
  DISPUTE_CLOSED: 'Candidate Portal Dispute Closed',
  DISPUTE_SUBMIT_FAILED: 'Candidate Portal Dispute Submit Failed',
  STORY_STARTED: 'Candidate Portal Story Started',
  STORY_STEP_VIEWED: 'Candidate Portal Story Step Viewed',
  STORY_CLOSED: 'Candidate Portal Story Closed',
  STORY_SUBMIT_FAILED: 'Candidate Portal Story Submit Failed',
  VERIFICATION_CARD_CLICKED: 'Candidate Portal Verification Card Clicked',
  MOBILE_MENU_OPENED: 'Candidate Portal Mobile Menu Opened',
  MENU_ITEM_CLICKED: 'Candidate Portal Menu Item Clicked',
  REPORT_CARD_CLICKED: 'Candidate Portal Report Card Clicked',
};

const CHECKR_PERSONAL_ANALYTICS_EVENTS = {
  LEGACY_BANNER_VIEWED: 'Checkr Verified - Candidate Portal CTA Viewed',
  LEGACY_BANNER_DISMISSED: 'Checkr Verified - Candidate Portal CTA Dismissed',
  LEGACY_BANNER_CTA_CLICKED: 'Checkr Verified - Candidate Portal CTA Clicked',
  PROFILES_BANNER_VIEWED: 'Checkr Verified - Setup Profile Banner Viewed',
  PROFILES_BANNER_DISMISSED: 'Checkr Verified - Setup Profile Banner Dismissed',
  PROFILES_BANNER_CTA_CLICKED:
    'Checkr Verified - Setup Profile Banner CTA Clicked',
};

const VERIFICATION_EVENT_TITLE = [
  ANALYTICS_EVENTS.EXCEPTION_FLOW_INFORMATION_COMPLETED,
  ANALYTICS_EVENTS.EXCEPTION_FLOW_WELCOME_PAGE_VIEWED,
  ANALYTICS_EVENTS.NOT_YOU_BUTTON_CLICKED,
];

const NO_EVENT_PROPS_EVENTS = [
  ANALYTICS_EVENTS.FAQ_SECTION_LINK_CLICKED,
  ANALYTICS_EVENTS.HELP_CENTER_VIEWED,
  ANALYTICS_EVENTS.LANGUAGE_SELECTED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_SSN_LOGIN_SUBMITTED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_SSN_LOGIN_SUCCESS,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_SSN_LOGIN_FAILED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_INTERNATIONAL_LOGIN_SUBMITTED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_INTERNATIONAL_LOGIN_SUCCESS,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_INTERNATIONAL_LOGIN_FAILED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_PASSWORDLESS_LOGIN_VIEWED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_PASSWORDLESS_LOGIN_CLICKED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_LOGIN_PAGE_VIEWED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_CODE_PAGE_VIEWED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_DOB_PAGE_VIEWED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_DOB_SUBMITTED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_DOB_VERIFY_FAILED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_LOGIN_SUBMITTED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_LOGIN_FAILED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_RESEND_CODE_CLICKED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_BACK_TO_EMAIL_CLICKED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_SSN_FALLBACK_CLICKED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_GOOGLE_OAUTH_INITIATED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_WORKOS_GOOGLE_OAUTH_FAILED,
  ANALYTICS_EVENTS.CANDIDATE_PORTAL_LOGIN_SUCCESS,
];

const USER_PROPERTIES = {
  APPLICANT_ID: 'Applicant ID',
  PAGE_DOMAIN: 'Page Domain',
  CANDIDATE_REGION: 'Candidate Region',
};

const ANALYTICS_PROPERTIES = {
  ACCOUNT_RESOURCE_ID: 'Account Resource ID',
  ACCOUNT_URI_NAME: 'Account URI Name',
  ATTRIBUTION_SOURCE: 'Attribution Source',
  CANDIDATE_RESOURCE_ID: 'Candidate Resource ID',
  CANDIDATE_DATABASE_SOURCE: 'Candidate Database Source',
  CONTENT_KEYS: 'Content Keys',
  CUSTOM_CANDIDATE_ID: 'Custom Candidate ID',
  EXCEPTION_FLOW_VERIFICATION_ID: 'Exception ID',
  EXCEPTION_TYPE: 'Exception Type',
  INVITATION_SOURCE: 'Invitation Source',
  LANGUAGE: 'Language',
  QUESTION: 'Question',
  REPORT_GEO_COUNTRY: 'Report Geo Country',
  REPORT_NAME: 'Report Name',
  REPORT_RESOURCE_ID: 'Report Resource ID',
  SCREENING_ARRAY: 'Screening Array',
  SCREENING_NAME: 'Screening Name',
  WORK_COUNTRY_LOCATION: 'Work Country Location',
  LINK: 'Clicked Link',
  PAGE_DOMAIN: 'Page Domain',
  CANDIDATE_REGION: 'Candidate Region',
  AUTH_METHOD: 'Auth Method',
  REPORT_COUNT: 'Report Count',
  IS_CANDIDATE: 'Is Candidate',
  VIEWPORT: 'Viewport',
  VIEWPORT_WIDTH: 'Viewport Width',
  APP_VERSION: 'App Version',
  DISPUTE_FLOW: 'Dispute Flow',
  STEP: 'Step',
  STEP_NUMBER: 'Step Number',
  PREVIOUSLY_DISPUTED: 'Previously Disputed',
  SUBMITTED: 'Submitted',
  VERIFICATION_STATE: 'Verification State',
  VERIFICATION_NAME: 'Verification Name',
  MENU_ITEM: 'Menu Item',
  REPORT_STATUS: 'Report Status',
  REPORT_POSITION: 'Report Position',
};

const DISPUTE_FLOWS = {
  MOTOR_VEHICLE_REPORT: 'Motor Vehicle Report',
  OTHER_SCREENINGS: 'Other Screenings',
};

const COUNTRIES_IN_US_DATA_CENTER = ['AS', 'GU', 'PH', 'PR', 'SG', 'US', 'VI'];
const LOCALE = 'en';

const SCREENING_NAME_MAPPING = {
  arrestSearch: 'Arrest Search',
  childAbuseSearches: 'Child Abuse Searches',
  clearinghouseSearch: 'Clearinghouse Search',
  countyCivilSearches: 'County Civil Searches',
  countyCriminalSearches: 'County Criminal Searches',
  creditReport: 'Credit Report',
  drugScreening: 'Drug Screening',
  educationVerification: 'Education Verification',
  employmentVerification: 'Employment Verification',
  evictionSearch: 'Eviction Search',
  facisSearch: 'Facis Search',
  federalCivilSearch: 'Federal Civil Search',
  federalCriminalSearch: 'Federal Criminal Search',
  federalDistrictCivilSearches: 'Federal District Civil Searches',
  federalDistrictCriminalSearches: 'Federal District Criminal Searches',
  globalWatchlistSearch: 'Global Watchlist Search',
  healthcareSanctionsSearches: 'Healthcare Sanctions Searches',
  internationalAdverseMediaSearches: 'Adverse Media',
  internationalCanadaBcEnhancedCriminalSearch: 'Canada Bc Enhanced Criminal',
  internationalCriminalSearches: 'International Criminal Searches',
  internationalCriminalSearchesV2: 'International Criminal',
  internationalEducationVerification: 'International Education',
  internationalEmploymentVerification: 'International Employment',
  internationalGlobalWatchlistSearch: 'Global Watchlist',
  internationalIdentityDocumentValidation:
    'International Identity Document Validation',
  internationalMotorVehicleReports: 'International Motor Vehicle Report',
  motorVehicleReport: 'Motor Vehicle Report',
  municipalCriminalSearches: 'Municipal Criminal Searches',
  nationalCriminalSearch: 'National Criminal Search',
  occupationalHealthScreening: 'Occupational Health Screening',
  personalReferenceVerifications: 'Personal Reference Verifications',
  pointerStateCriminalSearches: 'Pointer State Criminal Searches',
  professionalLicenseVerifications: 'Professional License Verifications',
  professionalReferenceVerifications: 'Professional Reference Verifications',
  program: 'Program',
  sexOffenderSearch: 'Sex Offender Search',
  ssnTrace: 'Ssn Trace',
  stateCriminalSearches: 'State Criminal Searches',
  terroristWatchlistSearch: 'Terrorist Watchlist Search',
};

const VERIFICATION_MODEL_TO_EXCEPTION_TYPE_MAPPING = {
  driver_license: 'Driver License Document Upload Exception',
  driver_license_history: 'Driver License History Document Upload Exception',
  education_proof: 'Education Proof Upload Exception',
  employment_proof: 'Employment Proof Upload Exception',
  location: 'Drug Schedule Location Form Exception',
  matrix_verification: 'Matrix Verification Exception',
  passport: 'Passport Document Upload Exception',
  previous_driver_license: 'Previous Driver License Upload Exception',
  selfie: 'Photo Verification Document Upload Exception',
  ssn_card: 'SSN Card Document Upload Exception',
  ssn_check_failure: 'SSN Check Failure Exception',
  ssn_confirmation: 'SSN Check Confirmation Form Exception',
  state_id_card: 'State ID Card Upload Exception',
};

const AMPLITUDE_NAME_MAP = {
  canada_bc_enhanced_criminal: 'Canada BC Enhanced Criminal',
  criminal: 'International Criminal',
  education: 'International Education',
  employment: 'International Employment',
  global: 'Global Watchlist',
  identity_document_validation: 'International Identity Document Validation',
  media_search: 'Adverse Media',
  motor_vehicle_report: 'International Motor Vehicle Report',
  right_to_work: 'International Right To Work',
};

const getCurrentReport = () => queryReportCache(getCurrentReportId());

const getUserPropertiesFromStore = (_, state) => {
  const userProps = {};
  const applicantId = state?.portal?.applicantId;
  userProps[USER_PROPERTIES.APPLICANT_ID] =
    applicantId === null ? null : applicantId ?? '';

  userProps[USER_PROPERTIES.PAGE_DOMAIN] = window?.location?.hostname;
  userProps[USER_PROPERTIES.CANDIDATE_REGION] = CHECKR_REGION || 'US';

  return userProps;
};

const getExceptionTypeArray = state => {
  const exceptionTypeArray = [];
  const stepsArray = state?.verifications?.steps;

  for (const step in stepsArray) {
    if (
      stepsArray[step].model in VERIFICATION_MODEL_TO_EXCEPTION_TYPE_MAPPING
    ) {
      exceptionTypeArray.push(
        VERIFICATION_MODEL_TO_EXCEPTION_TYPE_MAPPING[stepsArray[step].model],
      );
    }
  }

  return exceptionTypeArray;
};

const getEventPropertiesFromStore = (title, state) => {
  const eventProps = {};
  const report = getCurrentReport();
  eventProps[ANALYTICS_PROPERTIES.ACCOUNT_RESOURCE_ID] = report?.account?.id;
  eventProps[ANALYTICS_PROPERTIES.ACCOUNT_URI_NAME] = report?.account?.uriName;
  eventProps[ANALYTICS_PROPERTIES.CANDIDATE_RESOURCE_ID] =
    report?.candidate?.id;
  eventProps[ANALYTICS_PROPERTIES.CUSTOM_CANDIDATE_ID] =
    report?.candidate?.customId;
  const workLocationCountry =
    report?.candidate?.work_locations?.[0]?.country ??
    state?.portal?.country ??
    '';
  eventProps[ANALYTICS_PROPERTIES.REPORT_RESOURCE_ID] = report?.id;
  eventProps[ANALYTICS_PROPERTIES.WORK_COUNTRY_LOCATION] = countries.getName(
    workLocationCountry,
    LOCALE,
  );
  eventProps[ANALYTICS_PROPERTIES.REPORT_GEO_COUNTRY] = countries.getName(
    report?.candidate?.geos?.[0]?.country ?? state?.portal?.country ?? '',
    LOCALE,
  );
  eventProps[ANALYTICS_PROPERTIES.CANDIDATE_DATABASE_SOURCE] =
    COUNTRIES_IN_US_DATA_CENTER.includes(workLocationCountry) ? 'US' : 'EU';
  eventProps[ANALYTICS_PROPERTIES.SCREENING_ARRAY] = Object.entries(report)
    .map(([key, value]) =>
      Array.isArray(value) && value.length > 0
        ? SCREENING_NAME_MAPPING[key]
        : null,
    )
    .filter(Boolean);

  eventProps[ANALYTICS_PROPERTIES.PAGE_DOMAIN] = window?.location?.hostname;
  eventProps[ANALYTICS_PROPERTIES.CANDIDATE_REGION] = CHECKR_REGION || 'US';

  if (VERIFICATION_EVENT_TITLE.includes(title)) {
    eventProps[ANALYTICS_PROPERTIES.EXCEPTION_FLOW_VERIFICATION_ID] =
      state?.verifications?.verification?.id;
    eventProps[ANALYTICS_PROPERTIES.EXCEPTION_TYPE] =
      getExceptionTypeArray(state);
    eventProps[ANALYTICS_PROPERTIES.ACCOUNT_RESOURCE_ID] =
      state?.verifications?.verification?.accountId;
    eventProps[ANALYTICS_PROPERTIES.ACCOUNT_URI_NAME] =
      state?.verifications?.verification?.company;
  }

  if (ANALYTICS_EVENTS.LANDING_PAGE_VIEWED === title) {
    return {
      [ANALYTICS_PROPERTIES.PAGE_DOMAIN]: window?.location?.hostname,
      [ANALYTICS_PROPERTIES.CANDIDATE_REGION]: CHECKR_REGION || 'US',
      [ANALYTICS_PROPERTIES.REPORT_COUNT]: state?.portal?.reports?.length ?? 0,
      [ANALYTICS_PROPERTIES.IS_CANDIDATE]: state?.portal?.applicantId != null,
    };
  }

  if (NO_EVENT_PROPS_EVENTS.includes(title)) {
    return {};
  }
  return eventProps;
};

const VIEWPORT_PHONE_MAX = 671;
const VIEWPORT_TABLET_MAX = 1055;

const getViewport = width => {
  if (width <= VIEWPORT_PHONE_MAX) return 'phone';
  if (width <= VIEWPORT_TABLET_MAX) return 'tablet';
  return 'desktop';
};

const getClientEventProperties = () => {
  const width = window?.innerWidth;
  return {
    [ANALYTICS_PROPERTIES.VIEWPORT]: width ? getViewport(width) : undefined,
    [ANALYTICS_PROPERTIES.VIEWPORT_WIDTH]: width,
    [ANALYTICS_PROPERTIES.APP_VERSION]: APP_VERSION,
  };
};

const setAmplitudeUserProperty = (key, value) => {
  try {
    const event = new Identify();
    event.set(key, value);
    ampIdentify(event);
  } catch {
    // Analytics must never block the calling flow
  }
};

export {
  ANALYTICS_EVENTS,
  ANALYTICS_PROPERTIES,
  AMPLITUDE_NAME_MAP,
  CHECKR_PERSONAL_ANALYTICS_EVENTS,
  DISPUTE_FLOWS,
  getClientEventProperties,
  getViewport,
  getUserPropertiesFromStore,
  getEventPropertiesFromStore,
  setAmplitudeUserProperty,
};

export default checkrAnalytics;
